import { AgencyContract } from "./AgencyContract";
import { IndividualAcknowledgement } from "./IndividualAcknowledgement";

export interface SignedAgreementPayload {
  isAgency: boolean;
  lang: 'en' | 'ar';
  data: {
    applicantName: string;
    companyName: string;
    crNumber: string;
    vatNumber: string;
    email: string;
    personInChargeName: string;
    personInChargeTitle: string;
    nationalIdNumber: string;
    city: string;
  };
  signature: string | null;
  stamp: string | null;
  acceptedAt: Date | null;
  /** The date shown in the contract header. Falls back to acceptedAt or now. */
  documentDate?: Date | null;
}

interface SignedAgreementDocumentProps extends SignedAgreementPayload {
  /** Used inside the off-screen PDF sandbox to enforce a print-friendly width. */
  forPrint?: boolean;
}

/**
 * Single source of truth for the signed agreement: used both for the on-screen
 * preview inside `AgreementStep` and for the PDF generated in
 * `captureAgreementToPdf`. Guarantees 1:1 parity between what the user signs
 * and what is archived/emailed.
 */
export const SignedAgreementDocument = ({
  isAgency,
  lang,
  data,
  signature,
  stamp,
  acceptedAt,
  documentDate,
  forPrint,
}: SignedAgreementDocumentProps) => {
  const effectiveDate = documentDate ?? acceptedAt ?? new Date();
  const isRTL = lang === 'ar';

  const acceptanceTimestampLabel = lang === 'ar'
    ? 'تم القبول إلكترونياً في'
    : 'Electronically accepted on';
  const signatureLabel = lang === 'ar' ? 'التوقيع' : 'Signature';
  const stampLabel = lang === 'ar' ? 'الختم' : 'Stamp';

  const showFooter = (isAgency && (signature || stamp)) || (acceptedAt != null);

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      style={
        forPrint
          ? { width: '760px', padding: '24px', background: '#ffffff', color: '#0f172a' }
          : undefined
      }
    >
      {isAgency ? (
        <AgencyContract lang={lang} data={data} documentDate={effectiveDate} />
      ) : (
        <IndividualAcknowledgement lang={lang} data={data} documentDate={effectiveDate} />
      )}

      {showFooter && (
        <div className="mt-6 pt-4 border-t border-border space-y-3 text-sm">
          {isAgency && (signature || stamp) && (
            <div className="grid grid-cols-2 gap-4">
              {signature && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{signatureLabel}</p>
                  <div className="border border-border rounded p-2 bg-background">
                    <img src={signature} alt="signature" className="max-h-24 object-contain" />
                  </div>
                </div>
              )}
              {stamp && (
                <div>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{stampLabel}</p>
                  <div className="border border-border rounded p-2 bg-background">
                    <img src={stamp} alt="stamp" className="max-h-24 object-contain" />
                  </div>
                </div>
              )}
            </div>
          )}
          {acceptedAt && (
            <p className="text-xs text-muted-foreground">
              <strong>{acceptanceTimestampLabel}:</strong>{" "}
              {acceptedAt.toLocaleString(lang === 'ar' ? 'ar-SA' : 'en-GB')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default SignedAgreementDocument;
